import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND } from "@/lib/api";
import { useSEO, ORG_LD, FAQ_LD } from "@/lib/seo";
import { trackReserveClick } from "@/lib/analytics";
import { ArrowRight, Gift } from "lucide-react";
import TrustindexWidget from "@/components/TrustindexWidget";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import HomeVideoSection from "@/components/HomeVideoSection";
import { useI18n } from "@/i18n";
import { Rich } from "@/components/Rich";

const resolveUrl = (u) => (u?.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function Home() {
  const [signatures, setSignatures] = useState([]);
  const [imgs, setImgs] = useState({});
  const { t, lang, localePath, alternateUrls } = useI18n();

  useSEO({
    // Section I: canonical title for /
    title: lang === "en"
      ? "Restaurant & receptions in Ville-d'Avray"
      : "Restaurant & réceptions à Ville-d'Avray",
    description: lang === "en"
      ? "L'Entracte in Ville d'Avray: refined and creative French cuisine in a private townhouse. 240 m² flowered terrace, private events 12-100 guests. Book your table."
      : "L'Entracte à Ville d'Avray : cuisine française raffinée et créative dans un hôtel particulier. Terrasse fleurie de 240m², réceptions privées 12-100 personnes. Réservez votre table.",
    keywords: lang === "en"
      ? "French restaurant Ville d'Avray, fine dining Paris, private townhouse restaurant, terrace, private events, corporate seminar"
      : "restaurant Ville d'Avray, restaurant gastronomique 92, hôtel particulier, terrasse, événements privés, séminaire entreprise",
    // RESTAURANT_LD is no longer passed here — it lives as a static
    // <script id="ld-global-lieu"> in /public/index.html so it appears in
    // the raw HTML before React executes.
    jsonLd: [ORG_LD, FAQ_LD],
    lang,
    alternates: alternateUrls(),
  });

  useEffect(() => {
    (async () => {
      try {
        const [dRes, sRes] = await Promise.all([
          api.get("/menu/dishes"),
          api.get("/settings/public"),
        ]);
        setSignatures(dRes.data.filter((d) => d.is_signature).slice(0, 4));
        setImgs(sRes.data || {});
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const HERO_IMG = resolveUrl(imgs["home.hero_image"]);
  const CHEF_IMG = resolveUrl(imgs["home.chef_image"]);
  const TERRACE_IMG = resolveUrl(imgs["home.terrace_image"]);
  const EVENT_IMG = resolveUrl(imgs["home.event_image"]);
  const PLATE_IMG = resolveUrl(imgs["home.plate_image"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[100svh] min-h-[640px] flex items-end overflow-hidden -mt-[148px]">
        {HERO_IMG && <img src={HERO_IMG} alt="L'Entracte Restaurant" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/55" />
        <div className="relative z-10 max-w-[1500px] mx-auto w-full px-6 md:px-12 lg:px-16 pb-20 md:pb-28">
          <p data-testid="hero-overline" className="overline reveal text-[#E6CFA1]">{t("home.hero.overline")}</p>
          <h1
            data-testid="hero-title"
            className="font-display reveal reveal-delay-1 text-5xl sm:text-6xl md:text-7xl lg:text-[7rem] leading-[1.02] mt-6 max-w-5xl text-white font-light"
          >
            {t("home.hero.title1")}
            <br />
            <span className="italic text-[#E6CFA1]">{t("home.hero.title2")}</span>
          </h1>
          <p data-testid="hero-sub" className="reveal reveal-delay-2 mt-8 text-base md:text-lg text-white/85 max-w-xl">
            {t("home.hero.sub")}
          </p>
          <div className="reveal reveal-delay-3 mt-10 flex flex-wrap gap-4">
            <Link to={localePath("/reservation")} data-testid="hero-cta-reserve" onClick={() => trackReserveClick("hero")} className="btn-gold">
              {t("home.hero.ctaReserve")} <ArrowRight size={14} />
            </Link>
            <Link to={localePath("/menu")} data-testid="hero-cta-menu" className="btn-ghost-light">
              {t("home.hero.ctaMenu")}
            </Link>
          </div>
        </div>
      </section>

      {/* MOBILE GIFT CARD STRIP */}
      <Link
        to={localePath("/carte-cadeau")}
        data-testid="home-giftcard-strip"
        className="md:hidden flex items-center gap-3 px-5 py-3.5 bg-[#203240] border-y border-[#B8956A]/40 active:bg-[#1A2A36]"
      >
        <Gift size={18} className="shrink-0 text-[#B8956A]" />
        <p className="flex-1 text-[13px] leading-snug text-white/85">
          {t("home.giftStrip.text")}{" "}
          <span className="text-[#E6CFA1] underline underline-offset-2">{t("home.giftStrip.link")}</span>
        </p>
        <ArrowRight size={16} className="shrink-0 text-[#B8956A]" />
      </Link>

      <div className="sage-band py-6 text-center">
        <p className="text-[#F6F0E7] uppercase tracking-[0.5em] text-sm md:text-base font-light">{t("home.band1")}</p>
      </div>

      {/* ABOUT / STORY */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div className="md:col-span-5 order-1">
            <div className="aspect-[4/5] overflow-hidden">
              {CHEF_IMG && <img src={CHEF_IMG} alt="Cuisine du chef" className="w-full h-full object-cover img-soft" />}
            </div>
          </div>
          <div className="md:col-span-7 order-2 md:pl-8">
            <p className="overline">{t("home.about.overline")}</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-6 leading-tight text-[#203240] font-light">
              {t("home.about.title")} <span className="italic text-[#B8956A]">{t("home.about.titleItalic")}</span>.
            </h2>
            <div className="mt-8 space-y-5 text-[#3D4A5A] leading-relaxed text-base md:text-lg max-w-xl">
              <Rich as="p" value={t("home.about.p1")} />
              <Rich as="p" value={t("home.about.p2")} />
            </div>
            <div className="mt-10 grid grid-cols-3 gap-8 max-w-md">
              <div>
                <p className="font-display text-4xl text-[#B8956A]">240<span className="text-xl">m²</span></p>
                <p className="text-xs uppercase tracking-widest text-[#697585] mt-1">{t("home.about.stat1")}</p>
              </div>
              <div>
                <p className="font-display text-4xl text-[#B8956A]">80</p>
                <p className="text-xs uppercase tracking-widest text-[#697585] mt-1">{t("home.about.stat2")}</p>
              </div>
              <div>
                <p className="font-display text-4xl text-[#B8956A]">100<span className="text-xl">%</span></p>
                <p className="text-xs uppercase tracking-widest text-[#697585] mt-1">{t("home.about.stat3")}</p>
              </div>
            </div>
            <Link to={localePath("/menu")} data-testid="home-link-carte" className="btn-ghost mt-10">
              {t("home.about.cta")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <div className="sage-band py-6 text-center">
        <p className="text-[#F6F0E7] uppercase tracking-[0.5em] text-sm md:text-base font-light">{t("home.band2")}</p>
      </div>

      {/* EVENTS */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-white">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[5/6] overflow-hidden">
              {EVENT_IMG && <img src={EVENT_IMG} alt="Réception" className="w-full h-full object-cover img-soft" />}
            </div>
          </div>
          <div className="md:col-span-7 md:pl-10">
            <p className="overline">{t("home.events.overline")}</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight text-[#203240] font-light">
              {t("home.events.title")} <span className="italic text-[#B8956A]">{t("home.events.titleItalic")}</span>.
            </h2>
            <p className="mt-6 text-[#3D4A5A] text-base md:text-lg max-w-2xl leading-relaxed">
              {t("home.events.p")}
            </p>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl">
              {[
                { label: t("home.events.types")[0], to: "/mariage" },
                { label: t("home.events.types")[1], to: "/anniversaire" },
                { label: t("home.events.types")[2], to: "/bapteme" },
                { label: t("home.events.types")[3], to: "/seminaire" },
              ].map((it) => (
                <Link
                  key={it.to}
                  to={localePath(it.to)}
                  data-testid={`home-event-type-${it.to.replace("/", "")}`}
                  className="border border-[#D9D3C5] p-4 bg-[#F6F0E7]/50 hover:border-[#B8956A] transition-colors"
                >
                  <p className="font-display text-lg text-[#203240]">{it.label}</p>
                </Link>
              ))}
            </div>
            <Link to={localePath("/evenements")} data-testid="home-cta-events" className="btn-primary mt-10">
              {t("home.events.cta")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* MENU HIGHLIGHTS */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-[#F6F0E7] border-t border-[#D9D3C5]">
        <div className="max-w-[1500px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="overline">{t("home.menuHighlights.overline")}</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#203240] font-light">
                {t("home.menuHighlights.title")}<br/>
                <span className="italic text-[#B8956A]">{t("home.menuHighlights.titleItalic")}.</span>
              </h2>
            </div>
            <Link to={localePath("/menu")} data-testid="home-link-menu" className="btn-ghost self-start">
              {t("home.menuHighlights.cta")} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-7 space-y-2">
              {signatures.length === 0 && (
                <p className="text-[#697585]">{t("home.menuHighlights.loading")}</p>
              )}
              {signatures.map((d) => (
                <div key={d.id} data-testid={`signature-${d.id}`} className="py-4 border-b border-dotted border-[#C9C0AB]">
                  <div className="menu-leader">
                    <h3 className="font-display text-2xl text-[#203240]">{d.name}</h3>
                    <span className="dots" />
                    <span className="font-display text-xl text-[#B8956A]">{d.price.toFixed(2)} €</span>
                  </div>
                  {d.description && <p className="mt-2 text-[#697585] text-sm max-w-2xl">{d.description}</p>}
                </div>
              ))}
            </div>
            <div className="md:col-span-5 relative">
              <div className="aspect-[4/5] overflow-hidden">
                {PLATE_IMG && <img src={PLATE_IMG} alt="Plat signature" className="w-full h-full object-cover img-soft" />}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMMERSIVE VIDEO */}
      <HomeVideoSection
        videoUrl={imgs["home.video_url"]}
        poster={imgs["home.video_poster"]}
        title={lang === "en" ? (imgs["home.video_title_en"] || imgs["home.video_title"] || "Welcome to L'Entracte") : (imgs["home.video_title"] || "Bienvenue à L'Entracte")}
        subtitle={lang === "en" ? (imgs["home.video_sub_en"] || imgs["home.video_sub"] || "") : (imgs["home.video_sub"] || "")}
      />

      {/* TERRACE */}
      <section className="relative h-[80vh] min-h-[480px] overflow-hidden">
        {TERRACE_IMG && <img src={TERRACE_IMG} alt="La terrasse" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 flex items-end pb-20">
          <div className="max-w-2xl">
            <p className="overline text-[#E6CFA1]">{t("home.terrace.overline")}</p>
            <h2 className="font-display text-4xl md:text-6xl mt-4 leading-tight text-white font-light">
              {t("home.terrace.title")}
            </h2>
            <Link to={localePath("/galerie")} data-testid="home-cta-gallery" className="btn-ghost-light mt-8">
              {t("home.terrace.cta")}
            </Link>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section data-testid="home-reviews" className="px-6 md:px-12 lg:px-16 py-20 md:py-24 bg-white border-y border-[#D9D3C5]">
        <div className="max-w-[1500px] mx-auto">
          <div className="text-center mb-12">
            <p className="overline">{t("home.reviews.overline")}</p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[#203240] font-light">{t("home.reviews.title")}</h2>
          </div>
          <ReviewsCarousel className="max-w-[1200px] mx-auto" />
        </div>
      </section>

      {/* GIFT CARD BANNER */}
      <section data-testid="home-giftcard-banner" className="relative overflow-hidden bg-[#203240] px-6 md:px-12 lg:px-16 py-16 md:py-20">
        <div
          className="pointer-events-none absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full opacity-[0.22]"
          style={{ background: "radial-gradient(circle, #B8956A 0%, transparent 70%)" }}
        />
        <div
          className="pointer-events-none absolute -bottom-40 right-0 h-[460px] w-[460px] rounded-full opacity-[0.16]"
          style={{ background: "radial-gradient(circle, #E6CFA1 0%, transparent 70%)" }}
        />
        <div className="relative max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="overline text-[#E6CFA1] flex items-center gap-3">
              <Gift size={14} className="text-[#B8956A]" />
              {t("home.giftBanner.overline")}
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] mt-5 text-white font-light">
              {t("home.giftBanner.title1")}{" "}
              <span className="italic text-[#E6CFA1]">{t("home.giftBanner.title2")}</span>
            </h2>
            <p className="mt-6 text-white/70 text-sm md:text-base max-w-xl">
              {t("home.giftBanner.sub")}
            </p>
            <ul className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
              {["bullet1", "bullet2", "bullet3"].map((k) => (
                <li key={k} className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/60">
                  <span className="h-1 w-1 rounded-full bg-[#B8956A]" />
                  {t(`home.giftBanner.${k}`)}
                </li>
              ))}
            </ul>
            <Link
              to={localePath("/carte-cadeau")}
              data-testid="home-giftcard-cta"
              className="btn-gold mt-10 inline-flex items-center gap-2 group"
            >
              {t("home.giftBanner.cta")}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="lg:col-span-5">
            <Link
              to={localePath("/carte-cadeau")}
              data-testid="home-giftcard-visual"
              className="block mx-auto max-w-[420px] transition-transform duration-500 hover:-translate-y-1 hover:rotate-0 lg:rotate-[-2.5deg]"
            >
              <div className="relative border border-[#B8956A]/50 bg-[#1A2A36]/80 backdrop-blur-sm p-8 md:p-10 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.75)]">
                <div className="absolute inset-[10px] border border-[#B8956A]/20 pointer-events-none" />
                <p className="relative overline text-[#B8956A]">{t("home.giftBanner.cardLabel")}</p>
                <p className="relative font-display text-3xl md:text-4xl text-white mt-5 font-light">L'Entracte</p>
                <p className="relative mt-2 text-sm text-white/55 italic">{t("home.giftBanner.cardAmount")}</p>
                <div className="relative mt-8 pt-5 border-t border-dashed border-white/15 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
                    {t("home.giftBanner.cardFoot")}
                  </span>
                  <Gift size={18} className="text-[#B8956A]" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 md:px-12 lg:px-16 py-28 text-center bg-[#F6F0E7]">
        <p className="overline">{t("home.finalCta.overline")}</p>
        <h2 className="font-display text-5xl md:text-7xl mt-6 text-[#203240] font-light">
          <span className="italic text-[#B8956A]">{t("home.finalCta.title1")}</span>{t("home.finalCta.title2")}
        </h2>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to={localePath("/reservation")} data-testid="final-cta-reserve" onClick={() => trackReserveClick("home_final_cta")} className="btn-primary">{t("home.finalCta.reserve")}</Link>
          <Link to={localePath("/contact")} data-testid="final-cta-contact" className="btn-ghost">{t("home.finalCta.contact")}</Link>
        </div>
      </section>
    </div>
  );
}
